import React, { useEffect, useState } from "react";
import { Trash2, Loader2, Settings, CheckCircle2, Calendar, Plug } from "lucide-react";
import { toast } from "sonner";
import { sourceConnectors } from "./data";
import { Connector } from "../../../../components/types";
import { useNavigate } from "react-router-dom";
import { SetupGuide } from "../../../../components/marketing/connector/setup-guide";
import { ConnectorSelection } from "../../../../components/marketing/connector/connector-seletion";
import { useSources, useDeleteSource, AirbyteConnector } from "./airbyte-api";

// Matches a real connected source back to its catalog entry (for its emoji
// icon + a readable type name) by `definitionId` first — the one field
// that's unambiguous — falling back to a slug match against `sourceType`
// (Airbyte's public API returns this as the connector's docker repo short
// name, e.g. "facebook-marketing", which is exactly how this catalog's own
// `id`s are already written).
const resolveCatalogEntry = (source: AirbyteConnector): Connector | undefined =>
  sourceConnectors.find((c) => c.definitionId && c.definitionId === source.definitionId) ||
  sourceConnectors.find((c) => c.id.toLowerCase() === (source.sourceType || "").toLowerCase());

// `createdAt` isn't documented as seconds vs. milliseconds anywhere in this
// wrapper's spec — Airbyte's own public API uses epoch seconds, so this
// treats anything under the year-2001-in-ms threshold as seconds.
const formatConnectedDate = (createdAt?: number): string | null => {
  if (!createdAt) return null;
  const ms = createdAt < 1e12 ? createdAt * 1000 : createdAt;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const SourcesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSource, setSelectedSource] = useState<Connector | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedConnectorForGuide, setSelectedConnectorForGuide] = useState<Connector | null>(null);
  const [existingSourceForGuide, setExistingSourceForGuide] = useState<AirbyteConnector | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AirbyteConnector | null>(null);

  // Real GET /{org}/sources — this tenant's own created sources, separate
  // from the catalog of connector *types* below (sourceConnectors).
  const { data: sources, loading: sourcesLoading, error: sourcesError, reload: reloadSources } = useSources(true);
  const { remove: deleteSource, loading: deleting } = useDeleteSource();

  // Per direct ask: a failed load surfaces as a toast (the backend's own
  // message), not a persistent banner sitting above the rest of the page.
  useEffect(() => {
    if (sourcesError) toast.error(sourcesError);
  }, [sourcesError]);

  const handleConfigure = (id: string) => {};

  const filteredConnectors = sourceConnectors.filter((connector) => {
    return connector.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  function handleAddConnector() {
    navigate("/track/connections/new");
  }

  const handleCloseGuide = (): void => {
    setShowGuide(false);
    setSelectedConnectorForGuide(null);
    setExistingSourceForGuide(null);
  };

  // Opens that source's own configuration settings in the right-side panel
  // instead of redirecting to the setup guide route. `existing` is only set
  // from the "Configure" button on an already-connected source — its real
  // `.configuration` prefills the form instead of showing schema defaults.
  const handleShowGuide = (connector: Connector, existing?: AirbyteConnector): void => {
    setSelectedConnectorForGuide(connector);
    setExistingSourceForGuide(existing || null);
    setShowGuide(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleDelete = async (source: AirbyteConnector) => {
    const { ok, error } = await deleteSource(source.sourceId!);
    setPendingDelete(null);
    if (ok) {
      toast.success(`${source.name} disconnected.`);
      reloadSources();
    } else {
      toast.error(error || `Couldn't disconnect ${source.name}.`);
    }
  };

  return (
    <div className="min-h-screen my-5 bg-white dark:bg-gray-900 flex">
      <div className={`transition-all duration-300 px-6 ${showGuide ? "w-1/2" : "w-full"}`}>
        {/* Header - Removed the duplicate search input */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Sources</h1>
          </div>

          <button
            onClick={handleAddConnector}
            className="px-4 py-2 bg-blue-600 mt-5 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            Add Source
          </button>
        </div>

        {/* Real connected sources — GET /{org}/sources */}
        {sourcesLoading && sources.length === 0 ? (
          <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 mb-6">
            <Loader2 size={14} className="animate-spin" /> Loading your sources…
          </p>
        ) : sources.length > 0 ? (
          <div className="mb-8">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Connected Sources <span className="text-gray-400 dark:text-gray-500 font-normal">({sources.length})</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sources.map((s) => {
                const catalogEntry = resolveCatalogEntry(s);
                const dateLabel = formatConnectedDate(s.createdAt);
                return (
                  <div
                    key={s.sourceId}
                    className="flex items-start justify-between gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-200 dark:hover:border-gray-700 transition-colors"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-xl shrink-0">
                        {catalogEntry?.icon || <Plug size={16} className="text-gray-400" />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{s.name}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{catalogEntry?.name || s.sourceType}</p>
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
                          <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 size={11} /> Connected
                          </span>
                          {dateLabel && (
                            <span className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                              <Calendar size={11} /> {dateLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {catalogEntry && (
                        <button
                          onClick={() => handleShowGuide(catalogEntry, s)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                          title="Configure"
                        >
                          <Settings size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => setPendingDelete(s)}
                        disabled={deleting}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50"
                        title="Disconnect"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Stats */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            Available Sources • {sourceConnectors.length}+ integrations
          </p>
        </div>

        {/* Using the reusable ConnectorSelection component */}
        <ConnectorSelection
          title="Select Source"
          description="Choose a data source to connect from"
          connectors={filteredConnectors}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedConnector={selectedSource}
          onSelectConnector={setSelectedSource}
          onShowGuide={handleShowGuide}
          onConfigure={handleConfigure}
          placeholder="Search sources..."
          compact={showGuide}
        />
      </div>

      {showGuide && selectedConnectorForGuide && (
        <div className="w-1/2 border-l border-gray-300 dark:border-gray-700">
          <SetupGuide
            connector={selectedConnectorForGuide}
            onClose={handleCloseGuide}
            onSaved={reloadSources}
            existingConfiguration={existingSourceForGuide?.configuration}
            existingName={existingSourceForGuide?.name}
          />
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !deleting && setPendingDelete(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Disconnect {pendingDelete.name}?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
              This stops syncing data from this source and can't be undone. You'll need to reconnect it from scratch if you change your mind.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(pendingDelete)}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50"
              >
                {deleting && <Loader2 size={13} className="animate-spin" />}
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcesDashboard;
